import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';

/**
 * Determines whether the current session should be blocked from calling the Post Office
 * Placement API.
 *
 * Context: a large share of Post Office Placement API traffic (and the resulting Stargate
 * rate-limit alerts) comes from Confluence synthetic "test tenants" (e.g.
 * `confluence-frontend-*-sev1-tests.atlassian.net`). Jira already suppresses Post Office
 * placements for non-Post-Office synthetic accounts; this mirrors that behaviour for
 * Confluence so those test tenants stop bombarding the Placement API.
 *
 * Logic:
 * - Behind the `post-office-placement-synthetic-restriction-conf` feature gate so rollout
 *   can be controlled independently for Confluence.
 * - Only applies to synthetic tenants (detected via the existing
 *   `confluence_frontend_pollinator_synthetic_tenants` Statsig config, the same signal used
 *   to tag UFO `TenantType.Synthetic`).
 * - Post Office's own Pollinator accounts are allow-listed via the permanent
 *   `post-office-is-post-office-synthetic` gate (a segment of Post Office Pollinator
 *   accounts) so Post Office's own synthetic monitoring keeps working.
 *
 * @see https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/segments/segment%3Apost-office-pollinator-accounts
 */
export const isRestrictedFromPostOfficeSynthetic = (): boolean => {
	if (!fg('post-office-placement-synthetic-restriction-conf')) {
		return false;
	}

	const isSyntheticTenant = StatsigConfigurations.getBoolean(
		'confluence_frontend_pollinator_synthetic_tenants',
		false,
	);

	if (!isSyntheticTenant) {
		return false;
	}

	// Allow Post Office's own Pollinator accounts through (they rely on hitting the
	// Placement API for their own monitoring).
	return !fg('post-office-is-post-office-synthetic');
};
